'use client';

import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import {
  LinkCard,
  LinkCardIcon,
} from '@sola/ark-core/molecules/link-card/link-card.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import {
  Lightbox,
  LightboxTrigger,
} from '@sola/ark-skippy/molecules/lightbox/lightbox.tsx';
import { knownMyUrls } from '@sola/my-sdk/constants.ts';
import type {
  BlockProps,
  ComponentBaseProps,
} from '@sola/schema/lib/schema.ts';

import { BackButton } from '../../atoms/back-button/back-button.tsx';
import departureVideoPosterImage from '../../images/sponsor-a-child.webp';
import { DepartureSubSpotlightCard } from '../../molecules/departure-sub-spotlight-card/departure-sub-spotlight-card.tsx';
import { usePreloadNamespaces } from '../../utils/use-preload-namespaces.ts';
import { useDepartureSubOverview } from './departure-sub-overview.logic.ts';

export type DepartureSubOverviewProps = BlockProps & ComponentBaseProps;

const departureVideoUrl = 'https://vimeo.com/1191213979/52be700e02';

export const DepartureSubOverview: React.FC<DepartureSubOverviewProps> = () => {
  const { t } = useTranslation(['my']);

  // The lightbox mounts Video only after the supporter clicks Watch the Video.
  // Warm its skippy namespace while the page is idle so that interaction does
  // not suspend the page while i18next loads the translations.
  usePreloadNamespaces('skippy');

  const {
    acceptDisplayedChild,
    autoAcceptDate,
    decisionError,
    displayedChildGender,
    hasGlobalId,
    hasOverviewContent,
    handleAcceptDisplayedChild,
    isDecisionPending,
    displayedChildGlobalId,
    displayedChildName,
    getAlternateChildPath,
    isAlternateSelection,
    offeredChildName,
    offeredChildPath,
    selectAlternateChild,
    showError,
    spotlightData,
  } = useDepartureSubOverview();

  if (!hasGlobalId) {
    return null;
  }

  const watchVideoLabel = t(
    'my:sponsorshipRelationship.departures.subOverview.actions.watchVideo',
  );

  const offeredDescriptionKey = autoAcceptDate
    ? 'my:sponsorshipRelationship.departures.subOverview.description_withDate'
    : 'my:sponsorshipRelationship.departures.subOverview.description_withoutDate';

  return (
    <section className="DepartureSubOverview flex w-full flex-col gap-comfy-xl">
      {showError && (
        <>
          <BackButton
            url={knownMyUrls.sponsorshipsBase}
            label={t('my:sponsorshipRelationship.childBio.backButton.text')}
          />

          <Alert
            variant="negative"
            title={t(
              'my:sponsorshipRelationship.departures.subOverview.error.title',
            )}
            message={t(
              'my:sponsorshipRelationship.departures.subOverview.error.message',
            )}
          />
        </>
      )}

      {displayedChildGlobalId && hasOverviewContent && spotlightData && (
        <>
          {/* Header Content */}
          <header className="flex flex-col gap-comfy-lg max-w-md">
            <BackButton
              url={knownMyUrls.sponsorshipsBase}
              label={t('my:sponsorshipRelationship.childBio.backButton.text')}
            />

            <div className="flex w-full flex-col gap-snug-lg">
              <h1 className="m-none heading-neutral-amplified heading-md">
                {t(
                  isAlternateSelection
                    ? 'my:sponsorshipRelationship.departures.subOverview.alternate.heading'
                    : 'my:sponsorshipRelationship.departures.subOverview.heading',
                )}
              </h1>
              <div className="body-neutral-mild body-sm">
                <IntlBlock
                  i18nKey={
                    isAlternateSelection
                      ? 'my:sponsorshipRelationship.departures.subOverview.alternate.description'
                      : offeredDescriptionKey
                  }
                  context={displayedChildGender}
                  values={{
                    autoAcceptDate,
                    childName: displayedChildName,
                  }}
                  components={{ strong: <strong /> }}
                />
              </div>
            </div>

            <div className="flex flex-col gap-comfy-xs sm:flex-row sm:flex-wrap">
              <ButtonStyle
                iconBefore={<ArkIcon iconName="heart" />}
                isBusy={isDecisionPending}
                size="lg"
              >
                <button type="button" onClick={handleAcceptDisplayedChild}>
                  {t(
                    'my:sponsorshipRelationship.departures.subOverview.actions.accept',
                    { childName: displayedChildName },
                  )}
                </button>
              </ButtonStyle>

              {isAlternateSelection ? (
                <ButtonStyle size="lg" variant="secondary">
                  <Link href={offeredChildPath}>
                    {t(
                      'my:sponsorshipRelationship.departures.subOverview.alternate.switchBack',
                      { childName: offeredChildName },
                    )}
                  </Link>
                </ButtonStyle>
              ) : (
                <Lightbox
                  items={[
                    {
                      title: watchVideoLabel,
                      media: {
                        video: {
                          url: departureVideoUrl,
                          title: watchVideoLabel,
                          poster: {
                            alt: '',
                            height: departureVideoPosterImage.height,
                            url: departureVideoPosterImage.src,
                            width: departureVideoPosterImage.width,
                          },
                        },
                      },
                    },
                  ]}
                >
                  <LightboxTrigger ariaLabel={watchVideoLabel}>
                    <ButtonStyle
                      iconBefore={<ArkIcon iconName="play-circle" />}
                      size="lg"
                      variant="secondary"
                    >
                      <span>{watchVideoLabel}</span>
                    </ButtonStyle>
                  </LightboxTrigger>
                </Lightbox>
              )}
            </div>
          </header>

          {decisionError && (
            <Alert
              variant={decisionError === 'unavailable' ? 'warning' : 'negative'}
              title={t(
                `my:sponsorshipRelationship.departures.subOverview.decisionError.${decisionError}.title`,
              )}
              message={t(
                `my:sponsorshipRelationship.departures.subOverview.decisionError.${decisionError}.message`,
              )}
            />
          )}

          {/* Sub Info */}
          <DepartureSubSpotlightCard
            key={displayedChildGlobalId}
            availability={spotlightData.availability}
            childDetails={spotlightData.childDetails}
            departingChildDetails={spotlightData.departingChildDetails}
            getAlternateChildPath={getAlternateChildPath}
            onAccept={acceptDisplayedChild}
            onSelectAlternateChild={selectAlternateChild}
            variant={isAlternateSelection ? 'alternate' : 'offered'}
          />

          {/* Supporting Content */}
          <section className="flex w-full flex-col gap-comfy-sm @container">
            <div className="flex flex-col gap-snug-lg">
              <h2 className="m-none heading-neutral-amplified heading-md">
                {t(
                  'my:sponsorshipRelationship.departures.subOverview.contact.heading',
                )}
              </h2>
              <p className="m-none body-neutral-mild body-md">
                <IntlBlock
                  i18nKey="my:sponsorshipRelationship.departures.subOverview.contact.body"
                  components={{ strong: <strong /> }}
                />
              </p>
            </div>

            <div className="@lgc:w-1/2 mt-comfy-xl">
              <LinkCard
                href={t('my:ctas.contact.url')}
                graphic={
                  <LinkCardIcon>
                    <ArkIcon iconName="phone" />
                  </LinkCardIcon>
                }
                subtitle={t('my:ctas.contact.text')}
                title={t('my:ctas.contact.title')}
                staticPadding
              />
            </div>
          </section>
        </>
      )}
    </section>
  );
};
